import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import PropTypes from 'prop-types'
import { Field } from 'formik'
import sortBy from 'lodash/sortBy'
import startsWith from 'lodash/startsWith'

import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import Checkbox from '@components/Form/Checkbox'
import DatePicker from '@components/Form/DatePicker'
import Input from '@components/Form/Input'
import TextArea from '@components/Form/TextArea'

class CostItem extends Component {
  static propTypes = {
    staticData: PropTypes.object,
    feeFormStore: PropTypes.object,
    form: PropTypes.object,
    disabled: PropTypes.bool,
    hasFee: PropTypes.bool,
    minDeliveryDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    maxDeliveryDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    freeCall: PropTypes.bool
  }

  isCostTypeInputDisabled() {
    const { disabled, hasFee, feeFormStore } = this.props
    // Disable the input if the fee is a bundle or if it is being edited
    return disabled || hasFee || feeFormStore.fee.id
  }

  getEmployeeOptions = () => {
    const { staticData, feeFormStore } = this.props
    const { EMPLOYEE_POSITIONS } = staticData.CONST

    return feeFormStore.getEmployeesByPosition([
      EMPLOYEE_POSITIONS.ASSOCIATE,
      EMPLOYEE_POSITIONS.TEAM_LEADER
    ])
  }

  renderExpertInputs() {
    const { feeFormStore, disabled, hasFee } = this.props

    return (
      <Field
        name='expert'
        label='Expert'
        itemToString={item => item && item.label}
        loadOptions={searchString =>
          feeFormStore
            .expertTypeahead(searchString)
            .then(({ options }) => options)
        }
        disabled={hasFee || disabled}
        component={Autocomplete}
      />
    )
  }

  renderThirdPartyInputs() {
    const { disabled } = this.props

    return (
      <div className='m-t-lg m-b-xl'>
        <Field
          name='entityName'
          label='Entity name'
          disabled={disabled}
          component={Input}
        />
        <Field
          name='invoiceNumber'
          label='Invoice number'
          disabled={disabled}
          component={Input}
        />
      </div>
    )
  }

  renderCostTypeSpecificInputs() {
    const { form, staticData } = this.props

    const costTypeId = form.values.costTypeId
    const { costTypes } = staticData.names
    const costType = costTypes[costTypeId]

    if (startsWith(costType, 'Expert')) {
      return this.renderExpertInputs()
    }
    if (startsWith(costType, 'Third')) {
      return this.renderThirdPartyInputs()
    }
  }

  getCurrencyOptions = costTypeId => {
    const { staticData } = this.props
    const {
      EXPERT_HONORARIUM,
      EXPERT_TRAVEL_COST,
      EXPERT_OTHER,
      EXPERT_REFERRAL_COST,
      EXPERT_FREELANCE_WORK,
      EXPERT_VAT
    } = staticData.CONST.COST_TYPES
    const { EUR, USD, CNY, GBP } = staticData.CONST.CURRENCIES
    if (
      [
        EXPERT_HONORARIUM,
        EXPERT_TRAVEL_COST,
        EXPERT_OTHER,
        EXPERT_REFERRAL_COST,
        EXPERT_FREELANCE_WORK,
        EXPERT_VAT
      ].includes(costTypeId)
    ) {
      return staticData.currencies.filter(currency =>
        [EUR, USD, CNY, GBP].includes(currency.id)
      )
    }

    return staticData.currencies
  }

  render() {
    const {
      staticData,
      disabled,
      hasFee,
      minDeliveryDate,
      maxDeliveryDate,
      form,
      feeFormStore,
    } = this.props

    const freeCallCostTypeIds = [14, 15, 16, 17, 18, 19]
    const hasFreeCallCostType = item => freeCallCostTypeIds.includes(item.id)
    const costTypes = sortBy(staticData.costTypes, 'orderingPosition')

    const freeCallCostTypes = costTypes.filter(hasFreeCallCostType)

    const selectedItem = (id) => costTypes.find(costType => costType.id === id)
    return (
      <div className='expert-form__section'>
        <h2 className='expert-form__subtitle'>Cost details</h2>
        <Field
          name='costTypeId'
          placeholder='Select cost type'
          label='Cost type'
          options={costTypes}
          itemToString={item => item && item.name}
          disabled={this.isCostTypeInputDisabled()}
          component={Dropdown}
        />
        {this.renderCostTypeSpecificInputs()}
        {!hasFee ? (
          <div className='expert-form__input-group'>
            <label className='expert-form__label'>Cost recording date</label>
            <div className='expert-form__input-wrapper expert-form__input-wrapper__inline-children'>
              <Field
                name='deliveryDate'
                dateOnly
                minDate={disabled ? undefined : minDeliveryDate}
                maxDate={disabled ? undefined : maxDeliveryDate}
                disabled={disabled}
                component={DatePicker}
              />
              <div />
              <div />
            </div>
          </div>
        ) : null}
        <div className='expert-form__input-group'>
          <label className='expert-form__label'>Cost value</label>
          <div className='expert-form__input-wrapper expert-form__input-wrapper__inline-children'>
            <Field
              noLabel
              name='costCurrencyId'
              placeholder='Select currency'
              itemToString={item => item && item.name}
              options={this.getCurrencyOptions(form.values.costTypeId)}
              disabled={disabled}
              component={Dropdown}
            />
            <Field
              name='costValue'
              placeholder='Amount'
              short
              disabled={disabled}
              component={Input}
            />
            <div />
          </div>
        </div>
        {!hasFee ? (
          <Field
            name='isBillableToClient'
            label='Costs billable to client'
            disabled={disabled}
            component={Checkbox}
          />
        ) : null}
        <Field
          name='costNote'
          label='Note'
          placeholder=''
          disabled={disabled}
          component={TextArea}
        />
        {this.props.freeCall && (
          <Field
            name='freeCallCostTypeId'
            placeholder='Select a free call cost type'
            label='Free call cost type'
            options={freeCallCostTypes}
            itemToString={item => item && item.name}
            component={Dropdown}
            initialSelectedItem={selectedItem(feeFormStore.fee.freeCallCostTypeId)}
          />
        )}
      </div>
    )
  }
}

export default inject('staticData')(observer(CostItem))
